// pages/admin/Projects.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Users,
  DollarSign,
  Loader2,
  Briefcase,
  CheckCircle,
  XCircle,
  ChevronRight,
  X,
} from "lucide-react";
import {
  useGetAdminProjects,
  useDeleteProject,
} from "../../lib/api/adminProjects";
import { ProjectCategory } from "../../lib/types/adminProjects";

const CATEGORIES: ProjectCategory[] = [
  "AI Training",
  "Data Annotation",
  "Code Review",
  "Content Moderation",
  "Audio Transcription",
  "Sentiment Analysis",
  "Prompt Engineering",
  "Image Classification",
  "Text Review",
  "Video Annotation",
];

const AdminProjects: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data, isLoading } = useGetAdminProjects({
    search: debouncedSearch || undefined,
    category: selectedCategory || undefined,
    isActive: showInactive ? undefined : "true",
  });

  const { mutateAsync: deleteProjectMutation, isPending: isDeleting } =
    useDeleteProject();

  const projects = data?.data?.projects || [];

  const getCategoryConfig = (category: string) => {
    const configs: Record<string, { bg: string; text: string }> = {
      "AI Training": { bg: "bg-blue-100", text: "text-blue-700" },
      "Data Annotation": { bg: "bg-emerald-100", text: "text-emerald-700" },
      "Code Review": { bg: "bg-purple-100", text: "text-purple-700" },
      "Content Moderation": { bg: "bg-amber-100", text: "text-amber-700" },
      "Audio Transcription": { bg: "bg-rose-100", text: "text-rose-700" },
      "Sentiment Analysis": { bg: "bg-cyan-100", text: "text-cyan-700" },
      "Prompt Engineering": { bg: "bg-indigo-100", text: "text-indigo-700" },
      "Image Classification": { bg: "bg-teal-100", text: "text-teal-700" },
      "Text Review": { bg: "bg-orange-100", text: "text-orange-700" },
      "Video Annotation": { bg: "bg-pink-100", text: "text-pink-700" },
    };
    return configs[category] || { bg: "bg-gray-100", text: "text-gray-700" };
  };

  const getDifficultyConfig = (difficulty: string) => {
    switch (difficulty) {
      case "Easy":
        return { color: "text-emerald-600", bg: "bg-emerald-100" };
      case "Medium":
        return { color: "text-amber-600", bg: "bg-amber-100" };
      case "Hard":
        return { color: "text-red-600", bg: "bg-red-100" };
      default:
        return { color: "text-gray-600", bg: "bg-gray-100" };
    }
  };

  const handleDelete = async (projectId: string) => {
    try {
      await deleteProjectMutation(projectId);
      setDeleteConfirm(null);
      setOpenMenuId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleRowClick = (projectId: string) => {
    navigate(`/admin/projects/${projectId}`);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setShowInactive(false);
  };

  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openMenuId]);

  const hasActiveFilters = searchQuery || selectedCategory || showInactive;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                Projects
              </h1>
              <p className="text-sm sm:text-base text-gray-500 mt-1">
                Manage all projects and assignments
              </p>
            </div>
            {/* Desktop create button */}
            <button
              onClick={() => navigate("/admin/projects/create")}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all"
            >
              <Plus className="w-5 h-5" />
              Create Project
            </button>
          </div>

          {/* Mobile create button - full width */}
          <button
            onClick={() => navigate("/admin/projects/create")}
            className="sm:hidden w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25"
          >
            <Plus className="w-5 h-5" />
            Create Project
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Search and Filters */}
        <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border transition-all whitespace-nowrap ${
                showFilters || hasActiveFilters
                  ? "border-[#8B52FF] bg-[#8B52FF]/5 text-[#8B52FF]"
                  : "border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-[#8B52FF]" />
              )}
            </button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 sm:gap-4">
                <div className="w-full sm:w-auto sm:min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
                  >
                    <option value="">All Categories</option>
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2 cursor-pointer px-1 py-2 sm:px-3">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#8B52FF] focus:ring-[#8B52FF]"
                  />
                  <span className="text-sm text-gray-600">Show inactive</span>
                </label>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 underline sm:ml-auto"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Projects Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF]" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No projects found
            </h3>
            <p className="text-gray-500 mb-4 text-sm sm:text-base">
              {hasActiveFilters
                ? "Try adjusting your filters"
                : "Create your first project to get started"}
            </p>
            {!hasActiveFilters && (
              <button
                onClick={() => navigate("/admin/projects/create")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B52FF] text-white rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Create Project
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Difficulty
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Slots
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Assignments
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {projects.map((project) => {
                    const categoryConfig = getCategoryConfig(project.category);
                    const difficultyConfig = getDifficultyConfig(
                      project.difficulty
                    );

                    return (
                      <tr
                        key={project._id}
                        onClick={() => handleRowClick(project._id)}
                        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {project.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {project.projectId}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${categoryConfig.bg} ${categoryConfig.text}`}
                          >
                            {project.category}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1 text-gray-900">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">
                              {project.hourlyRate}
                            </span>
                            <span className="text-gray-500 text-sm">/hr</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${difficultyConfig.bg} ${difficultyConfig.color}`}
                          >
                            {project.difficulty}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1.5">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {project.filledSlots}/{project.totalSlots}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">
                              {project.pendingCount} pending
                            </span>
                            <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                              {project.activeCount} active
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {project.isActive ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              <XCircle className="w-3.5 h-3.5" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setMenuPosition({
                                top: rect.bottom + 4,
                                left: rect.right - 192,
                              });
                              setOpenMenuId(
                                openMenuId === project._id ? null : project._id
                              );
                            }}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="lg:hidden divide-y divide-gray-100">
              {projects.map((project) => {
                const categoryConfig = getCategoryConfig(project.category);
                const difficultyConfig = getDifficultyConfig(
                  project.difficulty
                );

                return (
                  <div
                    key={project._id}
                    onClick={() => handleRowClick(project._id)}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer active:bg-gray-100"
                  >
                    {/* Top Row: Title, Status, Menu */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {project.title}
                          </h3>
                          {project.isActive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              <XCircle className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {project.projectId}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuWidth = 192;
                          let left = rect.right - menuWidth;
                          if (left < 8) left = 8;
                          if (left + menuWidth > window.innerWidth - 8) {
                            left = window.innerWidth - menuWidth - 8;
                          }
                          setMenuPosition({
                            top: rect.bottom + 4,
                            left,
                          });
                          setOpenMenuId(
                            openMenuId === project._id ? null : project._id
                          );
                        }}
                        className="p-2 -mr-2 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>

                    {/* Category and Difficulty */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${categoryConfig.bg} ${categoryConfig.text}`}
                      >
                        {project.category}
                      </span>
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${difficultyConfig.bg} ${difficultyConfig.color}`}
                      >
                        {project.difficulty}
                      </span>
                    </div>

                    {/* Stats Row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">
                          ${project.hourlyRate}
                        </span>
                        <span className="text-gray-500">/hr</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>
                          {project.filledSlots}/{project.totalSlots} slots
                        </span>
                      </div>
                    </div>

                    {/* Assignments */}
                    <div className="flex items-center gap-2 mt-3">
                      <span className="px-2 py-1 rounded text-xs bg-amber-50 text-amber-700">
                        {project.pendingCount} pending
                      </span>
                      <span className="px-2 py-1 rounded text-xs bg-emerald-50 text-emerald-700">
                        {project.activeCount} active
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Context Menu */}
      {openMenuId && menuPosition && (
        <>
          <div
            className="fixed inset-0 z-[100]"
            onClick={() => {
              setOpenMenuId(null);
              setMenuPosition(null);
            }}
          />
          <div
            className="fixed w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-[101] py-1"
            style={{
              top: menuPosition.top,
              left: Math.max(
                8,
                Math.min(menuPosition.left, window.innerWidth - 200)
              ),
            }}
          >
            {(() => {
              const project = projects.find((p) => p._id === openMenuId);
              if (!project) return null;

              return (
                <>
                  <button
                    onClick={() => {
                      navigate(`/admin/projects/${project._id}`);
                      setOpenMenuId(null);
                      setMenuPosition(null);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  <button
                    onClick={() => {
                      navigate(`/admin/projects/${project._id}/edit`);
                      setOpenMenuId(null);
                      setMenuPosition(null);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Project
                  </button>
                  <hr className="my-1 border-gray-100" />
                  <button
                    onClick={() => {
                      setDeleteConfirm(project._id);
                      setOpenMenuId(null);
                      setMenuPosition(null);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Project
                  </button>
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-2xl p-5 sm:p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Project?
              </h3>
            </div>
            <p className="text-gray-500 mb-6 text-sm sm:text-base">
              This action cannot be undone. Projects with active assignments
              cannot be deleted.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="w-full sm:w-auto px-4 py-2.5 text-gray-600 hover:text-gray-800 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
                className="w-full sm:w-auto px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
